/**
 * Ajustes de APARÊNCIA da câmera (SPEC-20260918-0040).
 *
 * O painel "Aparência" do Meet, na parte que dá para fazer com honestidade:
 * suavizar a pele, clarear e dar nitidez. Ficaram de fora, por limite de
 * tecnologia e não de vontade, a maquiagem (exige malha facial de 468 pontos e
 * um projeto próprio) e a iluminação de estúdio direcional (é modelo neural de
 * reiluminação, não ajuste de brilho).
 *
 * A escolha vive no navegador, por computador — como a dos aparelhos e a do
 * fundo. Iluminação e câmera são do lugar onde a pessoa está, não da conta.
 */

export type Aparencia = {
  /** Suavizar a pele, de 0 (nada) a 1 (máximo). */
  suavizar: number
  /** Clarear, de 0 (nada) a 1 (máximo). */
  clarear: number
  /** Nitidez, de 0 (nada) a 1 (máximo). */
  nitidez: number
}

export const APARENCIA_NEUTRA: Aparencia = { suavizar: 0, clarear: 0, nitidez: 0 }

/**
 * Os NÍVEIS de retoque, em vez de uma barra contínua.
 *
 * O usuário mostrou o painel do Meet (2026-09-18 01:02) e o retoque lá não é
 * contínuo: é um interruptor e dois níveis com nome, "Sutil" e "Suave". Faz
 * sentido — ninguém sabe dizer se quer 37% de suavização, mas todo mundo sabe
 * escolher entre pouco e mais.
 *
 * O valor por trás continua sendo número, então o pipeline de vídeo não muda:
 * só a forma de escolher é que passa a ser nomeada.
 */
export const NIVEIS_DE_RETOQUE = [
  { id: 'sutil', nome: 'Sutil', valor: 0.3 },
  { id: 'suave', nome: 'Suave', valor: 0.6 },
] as const

export type NivelDeRetoque = (typeof NIVEIS_DE_RETOQUE)[number]['id']

/** Qual nível corresponde ao valor guardado (o mais próximo). */
export function nivelDoRetoque(suavizar: number): NivelDeRetoque {
  if (suavizar <= 0) return 'sutil'
  let melhor: NivelDeRetoque = NIVEIS_DE_RETOQUE[0].id
  let distancia = Math.abs(NIVEIS_DE_RETOQUE[0].valor - suavizar)
  for (const n of NIVEIS_DE_RETOQUE) {
    const d = Math.abs(n.valor - suavizar)
    if (d < distancia) {
      distancia = d
      melhor = n.id
    }
  }
  return melhor
}

export function valorDoNivel(id: NivelDeRetoque): number {
  return NIVEIS_DE_RETOQUE.find((n) => n.id === id)?.valor ?? 0
}

const CHAVE = 'orbind.chamada.aparencia'

/** Nada ligado? Então o processamento não precisa nem existir. */
export function aparenciaEstaNeutra(a: Aparencia): boolean {
  return a.suavizar <= 0 && a.clarear <= 0 && a.nitidez <= 0
}

const naFaixa = (v: unknown): number => {
  const n = typeof v === 'number' ? v : 0
  return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0
}

export function aparenciaGuardada(): Aparencia {
  try {
    const cru = localStorage.getItem(CHAVE)
    if (!cru) return APARENCIA_NEUTRA
    const o = JSON.parse(cru) as Partial<Aparencia>
    return { suavizar: naFaixa(o.suavizar), clarear: naFaixa(o.clarear), nitidez: naFaixa(o.nitidez) }
  } catch {
    return APARENCIA_NEUTRA
  }
}

export function guardarAparencia(a: Aparencia): void {
  try {
    if (aparenciaEstaNeutra(a)) localStorage.removeItem(CHAVE)
    else localStorage.setItem(CHAVE, JSON.stringify(a))
  } catch {
    /* sem storage: vale só nesta chamada */
  }
}

/* ------------------------------------------ como isso vira imagem --- */

/**
 * O CLAREAR é um filtro de canvas direto. Meio da escala dá um ganho parecido
 * com o que estava fixo antes (1,15 de brilho); o topo vai a 1,35, que já
 * recupera rosto contra janela.
 *
 * O contraste sobe junto, mas MENOS que o brilho: clarear sozinho lava a
 * imagem, e um toque de contraste devolve corpo sem endurecer a pele.
 */
export function filtroDeLuz(a: Aparencia): string {
  if (a.clarear <= 0) return ''
  const brilho = 1 + a.clarear * 0.35
  const contraste = 1 + a.clarear * 0.12
  return `brightness(${brilho.toFixed(3)}) contrast(${contraste.toFixed(3)})`
}

/**
 * O SUAVIZAR é foco suave: a mesma imagem desfocada, por cima da nítida, com
 * transparência. Não é o retoque do Meet, que sabe onde está o rosto — aqui
 * suaviza tudo, então o grau máximo é contido de propósito: acima disso a
 * imagem vira aquarela e os olhos perdem definição.
 *
 * A primeira calibragem era pesada demais: o usuário viu e disse "parece que a
 * imagem desfoca" (2026-09-18 21:03). Com razão — "Suave" dava 4,5 px de
 * desfoque a 39% de opacidade, o que é véu, não retoque. A escala caiu para
 * pouco mais da metade: o efeito precisa ser notado no rosto, não na imagem.
 */
export function desfoqueDaPele(a: Aparencia): number {
  return a.suavizar <= 0 ? 0 : 1 + a.suavizar * 2.6
}

export function opacidadeDaSuavizacao(a: Aparencia): number {
  return a.suavizar <= 0 ? 0 : a.suavizar * 0.34
}

/**
 * A NITIDEZ é uma convolução 3x3 — realce de bordas clássico. O canvas 2D não
 * tem filtro de nitidez, então isto é feito por um filtro SVG referenciado em
 * `ctx.filter`, que o Chromium aceita.
 *
 * O peso central cresce com o grau e as laterais compensam, para a soma da
 * matriz continuar 1 e a imagem não escurecer nem estourar.
 */
export function matrizDeNitidez(a: Aparencia): string {
  if (a.nitidez <= 0) return ''
  const lado = -a.nitidez * 0.6
  const centro = 1 + a.nitidez * 2.4
  return [0, lado, 0, lado, centro, lado, 0, lado, 0].map((n) => n.toFixed(3)).join(' ')
}

/** Rótulo de cada ajuste para a tela, com o valor em porcentagem. */
export function rotuloDoGrau(v: number): string {
  return v <= 0 ? 'desligado' : `${Math.round(v * 100)}%`
}
